import { useState } from 'react'
import {
  ajouterEcritureManuelle,
  enregistrerCompte,
  enregistrerType,
  supprimerCompte,
  supprimerType,
  type CompteSyscohada,
  type GrandLivre,
  type PlanComptable,
  type TypeTransaction,
} from '../../api/compta'
import { tableauFlux } from '../../api/flux'
import { BILAN_ACTIF as POSTES_ACTIF } from '../../api/ohada'
import { fcfa } from '../Format'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'

/**
 * Les QUATRE sous-onglets de la comptabilité qui manquaient au portage :
 * flux de trésorerie, plan comptable, types d'écritures, saisies manuelles.
 *
 * L'original en a neuf, le portage en avait cinq. Ce n'était pas un oubli
 * cosmétique : le tableau de flux est le TROISIÈME état de synthèse exigé par
 * l'OHADA, et sans les saisies manuelles aucune charge hors commandes —
 * assurance, hébergement, marketing — n'entre jamais dans le résultat.
 */

/** Intitulés officiels des classes SYSCOHADA. */
const CLASSES: Record<number, string> = {
  1: 'Classe 1 — Comptes de ressources durables',
  2: "Classe 2 — Comptes d'actif immobilisé",
  3: 'Classe 3 — Comptes de stocks',
  4: 'Classe 4 — Comptes de tiers',
  5: 'Classe 5 — Comptes de trésorerie',
  6: 'Classe 6 — Comptes de charges des activités ordinaires',
  7: 'Classe 7 — Comptes de produits des activités ordinaires',
  8: 'Classe 8 — Comptes des autres charges et produits (HAO)',
  9: 'Classe 9 — Comptes des engagements & comptabilité analytique',
}

// ── Flux de trésorerie ───────────────────────────────────────────────────

export function Flux({
  resultat,
  bilan,
}: {
  resultat: Record<string, number>
  bilan: Record<string, number>
}) {
  const { lignes, cloture, controle } = tableauFlux(resultat, bilan)
  const coherent = Math.abs(cloture - controle) < 1

  return (
    <div className="card">
      <div className="card-header">
        Tableau des flux de trésorerie — modèle OFFICIEL OHADA (Système Normal)
      </div>
      <div className="card-body flush">
        <table style={{ width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 70 }}>Réf</th>
              <th style={{ textAlign: 'left' }}>Libellé</th>
              <th style={{ textAlign: 'right', width: 150 }}>Exercice</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l, i) =>
              l.section ? (
                <tr key={i}>
                  <td
                    colSpan={3}
                    style={{
                      padding: '10px 12px 4px',
                      fontWeight: 700,
                      fontSize: 11.5,
                      textTransform: 'uppercase',
                      letterSpacing: '.4px',
                      color: 'var(--n700)',
                    }}
                  >
                    {l.section}
                  </td>
                </tr>
              ) : (
                <tr
                  key={i}
                  style={{
                    ...(l.grand ? { background: 'var(--cream)' } : {}),
                    ...(l.total || l.grand ? { borderTop: '2px solid var(--n700)' } : {}),
                  }}
                >
                  <td className="acc-code" style={{ textAlign: 'center' }}>
                    {l.ref}
                    {l.note && (
                      <span style={{ fontWeight: 400, color: 'var(--n400)' }}> {l.note}</span>
                    )}
                  </td>
                  <td
                    style={{
                      fontWeight: l.grand ? 800 : l.total ? 700 : 400,
                      fontSize: l.grand ? 14 : l.total ? 13 : 12.5,
                      ...(l.grand ? { textTransform: 'uppercase', letterSpacing: '.3px' } : {}),
                    }}
                  >
                    {l.libelle}
                  </td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: l.grand ? 800 : l.total ? 700 : 400,
                      fontSize: l.grand ? 14 : l.total ? 13 : 12.5,
                      color: `var(${(l.valeur ?? 0) >= 0 ? '--ink' : '--danger'})`,
                    }}
                  >
                    {fcfa(l.valeur ?? 0)}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {/* Le seul garde-fou automatique de l'état : la trésorerie reconstituée
          par les flux doit égaler celle du bilan. Un écart signale une écriture
          qui ne passe pas par le bon compte. */}
      <div
        className="card-body"
        style={{ borderTop: '1px solid var(--n200)', fontSize: 12, color: 'var(--n400)' }}
      >
        <b>Contrôle :</b> trésorerie de clôture calculée (ZH = {fcfa(cloture)}){' '}
        {coherent ? (
          <span style={{ color: 'var(--green)' }}>
            = trésorerie réelle du bilan ({fcfa(controle)}) ✔
          </span>
        ) : (
          <span style={{ color: 'var(--danger)' }}>
            ≠ trésorerie réelle du bilan ({fcfa(controle)}) — écart de{' '}
            {fcfa(Math.abs(cloture - controle))}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * Les deux modales d'édition du plan comptable.
 *
 * ⚠️ Elles manquaient au portage, et l'absence ne se voyait pas : les deux
 * écrans s'affichaient parfaitement, en LECTURE SEULE. C'est le détecteur de
 * parité (`verifier-parite.mjs`) qui l'a signalée, par une seule classe — la
 * `btn-danger` des boutons « Supprimer », utilisée deux fois dans l'original et
 * nulle part dans le portage.
 *
 * Sans elles, ajouter une charge nouvelle — une assurance, un loyer — oblige à
 * écrire dans la base à la main. C'est précisément ce qu'un responsable
 * non-technicien ne peut pas faire, et le module comptable perd son intérêt.
 */

/** Barre d'actions commune : Supprimer à gauche, Annuler / Enregistrer à droite. */
function ActionsModale({
  surSupprimer,
  surAnnuler,
  surEnregistrer,
  enCours,
}: {
  surSupprimer?: (() => void) | undefined
  surAnnuler: () => void
  surEnregistrer: () => void
  enCours: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
      {surSupprimer && (
        <button className="btn btn-danger btn-sm" onClick={surSupprimer} disabled={enCours}>
          Supprimer
        </button>
      )}
      <div style={{ flex: 1 }} />
      <button className="btn btn-outline btn-sm" onClick={surAnnuler} disabled={enCours}>
        Annuler
      </button>
      <button className="btn btn-accent btn-sm" onClick={surEnregistrer} disabled={enCours}>
        {enCours ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </div>
  )
}

function ModaleCompte({
  compte,
  nouveau,
  surFermer,
  surRecharger,
}: {
  compte: CompteSyscohada | null
  nouveau: boolean
  surFermer: () => void
  surRecharger: () => Promise<void>
}) {
  const ouverte = nouveau || compte !== null
  const [code, setCode] = useState(compte?.code ?? '')
  const [libelle, setLibelle] = useState(compte?.label ?? '')
  const [classe, setClasse] = useState(String(compte?.class ?? 6))
  const [nature, setNature] = useState(compte?.kind ?? '')
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function agir(action: () => Promise<void>) {
    setEnCours(true)
    setErreur(null)
    try {
      await action()
      await surRecharger()
      surFermer()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale
      titre={nouveau ? 'Nouveau compte' : 'Éditer le compte'}
      ouverte={ouverte}
      surFermer={surFermer}
    >
      <label>Code SYSCOHADA</label>
      {/* Le code est la CLÉ du compte : le modifier ne renommerait pas la ligne,
          il en créerait une seconde et laisserait les écritures pointer sur
          l'ancienne. D'où le champ figé en édition, comme dans l'original. */}
      <input
        value={code}
        disabled={!nouveau}
        placeholder="ex : 6182"
        onChange={(e) => setCode(e.target.value)}
      />
      <label>Libellé</label>
      <input value={libelle} onChange={(e) => setLibelle(e.target.value)} />
      <label>Classe</label>
      <ListeDeroulante
        valeur={classe}
        pleineLargeur
        options={[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
          valeur: String(n),
          libelle: `Classe ${n}`,
        }))}
        surChangement={setClasse}
      />
      <label>Nature</label>
      <input
        value={nature}
        placeholder="tiers | tresorerie | charge | produit (optionnel)"
        onChange={(e) => setNature(e.target.value)}
      />

      {erreur && (
        <p style={{ color: 'var(--danger)', fontSize: 12.5, whiteSpace: 'pre-line', marginTop: 12 }}>
          {erreur}
        </p>
      )}

      <ActionsModale
        enCours={enCours}
        surAnnuler={surFermer}
        surSupprimer={
          compte
            ? () => void agir(() => supprimerCompte(compte.code))
            : undefined
        }
        surEnregistrer={() =>
          void agir(() =>
            enregistrerCompte({
              code,
              label: libelle,
              class: parseInt(classe, 10) || 6,
              kind: nature,
            }),
          )
        }
      />
    </Modale>
  )
}

function ModaleType({
  type,
  nouveau,
  comptes,
  surFermer,
  surRecharger,
}: {
  type: TypeTransaction | null
  nouveau: boolean
  comptes: CompteSyscohada[]
  surFermer: () => void
  surRecharger: () => Promise<void>
}) {
  const ouverte = nouveau || type !== null
  const premier = comptes[0]?.code ?? ''
  const [code, setCode] = useState(type?.code ?? '')
  const [nom, setNom] = useState(type?.name ?? '')
  const [debit, setDebit] = useState(type?.debit ?? premier)
  const [credit, setCredit] = useState(type?.credit ?? premier)
  const [nature, setNature] = useState(type?.kind ?? 'revenue')
  const [auto, setAuto] = useState(type?.auto ?? true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  const options = comptes.map((a) => ({ valeur: a.code, libelle: `${a.code} — ${a.label}` }))

  async function agir(action: () => Promise<void>) {
    setEnCours(true)
    setErreur(null)
    try {
      await action()
      await surRecharger()
      surFermer()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e))
    } finally {
      setEnCours(false)
    }
  }

  return (
    <Modale
      titre={nouveau ? "Nouveau type d'écriture" : "Éditer le type d'écriture"}
      ouverte={ouverte}
      surFermer={surFermer}
    >
      <label>Code</label>
      <input
        value={code}
        disabled={!nouveau}
        placeholder="ex : PACKAGING"
        onChange={(e) => setCode(e.target.value.toUpperCase())}
      />
      <label>Nom</label>
      <input value={nom} onChange={(e) => setNom(e.target.value)} />
      <label>Compte de débit</label>
      <ListeDeroulante valeur={debit} options={options} surChangement={setDebit} pleineLargeur />
      <label>Compte de crédit</label>
      <ListeDeroulante valeur={credit} options={options} surChangement={setCredit} pleineLargeur />
      <label>Nature</label>
      <ListeDeroulante
        valeur={nature}
        pleineLargeur
        options={[
          { valeur: 'revenue', libelle: 'Produit' },
          { valeur: 'expense', libelle: 'Charge' },
          { valeur: 'passthrough', libelle: 'Transit' },
          /* « Taxe » depuis la migration 0039 : la TVA n'est ni un produit ni
             une charge, et ce genre l'écarte du chiffre d'affaires par
             construction. L'omettre ici rendrait le genre inatteignable
             depuis l'écran. */
          { valeur: 'tax', libelle: 'Taxe' },
          /* « Trésorerie » depuis 0044 : encaissement, remboursement, paie ou
             reversement versés — de l'argent qui bouge, sans produit ni charge. */
          { valeur: 'cash', libelle: 'Trésorerie' },
        ]}
        surChangement={setNature}
      />
      <label
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontWeight: 600 }}
      >
        <input
          type="checkbox"
          checked={auto}
          style={{ width: 'auto' }}
          onChange={(e) => setAuto(e.target.checked)}
        />
        Générer automatiquement depuis les événements
      </label>
      {/* Cocher « auto » sur un type qu'on saisit aussi à la main le compterait
          DEUX fois au résultat : une fois généré depuis les commandes, une fois
          saisi. L'écran des saisies manuelles ne propose d'ailleurs que les
          types non-auto, pour cette raison exacte. */}

      {erreur && (
        <p style={{ color: 'var(--danger)', fontSize: 12.5, whiteSpace: 'pre-line', marginTop: 12 }}>
          {erreur}
        </p>
      )}

      <ActionsModale
        enCours={enCours}
        surAnnuler={surFermer}
        surSupprimer={type ? () => void agir(() => supprimerType(type.code)) : undefined}
        surEnregistrer={() =>
          void agir(() => enregistrerType({ code, name: nom, debit, credit, kind: nature, auto }))
        }
      />
    </Modale>
  )
}

// ── Plan comptable ───────────────────────────────────────────────────────

export function PlanComptableVue({
  plan,
  gl,
  surRecharger,
}: {
  plan: PlanComptable
  gl: GrandLivre
  surRecharger: () => Promise<void>
}) {
  const [edite, setEdite] = useState<CompteSyscohada | null>(null)
  const [nouveau, setNouveau] = useState(false)
  const parClasse = new Map<number, PlanComptable['comptes']>()
  for (const c of plan.comptes) {
    const l = parClasse.get(c.class) ?? []
    l.push(c)
    parClasse.set(c.class, l)
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--n700)', flex: 1 }}>
          <b>Plan comptable SYSCOHADA</b> — classes 1 à 9, libellés officiels. Les comptes surlignés
          sont ceux que la plateforme mouvemente. Cliquer un compte pour l'éditer.
        </div>
        <button className="btn btn-accent btn-sm" onClick={() => setNouveau(true)}>
          + Nouveau compte
        </button>
      </div>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((classe) => {
        const liste = [...(parClasse.get(classe) ?? [])].sort((a, b) =>
          String(a.code).localeCompare(String(b.code)),
        )
        if (!liste.length) return null
        return (
          <div className="card" style={{ marginBottom: 12 }} key={classe}>
            <div className="card-header" style={{ fontSize: 13 }}>
              {CLASSES[classe]}
              <span style={{ color: 'var(--n400)', fontWeight: 400, marginLeft: 6 }}>
                {liste.length} comptes
              </span>
            </div>
            <div className="card-body flush">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Code</th>
                    <th>Libellé SYSCOHADA</th>
                    <th style={{ textAlign: 'right', width: 130 }}>Solde</th>
                  </tr>
                </thead>
                <tbody>
                  {liste.map((a) => {
                    const solde = gl[a.code]?.solde ?? 0
                    return (
                      <tr key={a.code} style={{ cursor: 'pointer' }} onClick={() => setEdite(a)}>
                        <td>
                          <span className="acc-code">{a.code}</span>
                        </td>
                        <td>{a.label}</td>
                        <td style={{ textAlign: 'right' }}>{solde ? fcfa(solde) : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}

      {(edite || nouveau) && (
        <ModaleCompte
          compte={edite}
          nouveau={nouveau}
          surRecharger={surRecharger}
          surFermer={() => {
            setEdite(null)
            setNouveau(false)
          }}
        />
      )}
    </>
  )
}

// ── Types d'écritures ────────────────────────────────────────────────────

export function TypesEcritures({
  plan,
  surRecharger,
}: {
  plan: PlanComptable
  surRecharger: () => Promise<void>
}) {
  const [edite, setEdite] = useState<TypeTransaction | null>(null)
  const [nouveau, setNouveau] = useState(false)

  return (
    <div className="card">
      <div className="card-header">
        Types d'ecritures — regles debit/credit (ajout admin, zero code)
        <div style={{ flex: 1 }} />
        <button className="btn btn-accent btn-sm" onClick={() => setNouveau(true)}>
          + Nouveau type
        </button>
      </div>
      <div className="card-body flush">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Nom</th>
              <th>Debit</th>
              <th>Credit</th>
              <th>Nature</th>
              {/* « Auto » distingue les écritures GÉNÉRÉES depuis les commandes
                  de celles qu'on saisit. Sans cette colonne, rien n'indique
                  lesquelles seraient comptées deux fois si on les saisissait. */}
              <th>Auto</th>
            </tr>
          </thead>
          <tbody>
            {plan.types.map((t) => (
              <tr key={t.code} style={{ cursor: 'pointer' }} onClick={() => setEdite(t)}>
                <td>
                  <span className="jrn-pill">{t.code}</span>
                </td>
                <td>{t.name}</td>
                <td>
                  <span className="acc-code">{t.debit}</span>
                </td>
                <td>
                  <span className="acc-code">{t.credit}</span>
                </td>
                <td>{t.kind}</td>
                <td>
                  {t.auto ? (
                    <span className="badge badge-green">Auto</span>
                  ) : (
                    <span className="badge badge-gray">Manuel</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(edite || nouveau) && (
        <ModaleType
          type={edite}
          nouveau={nouveau}
          comptes={plan.comptes}
          surRecharger={surRecharger}
          surFermer={() => {
            setEdite(null)
            setNouveau(false)
          }}
        />
      )}
    </div>
  )
}

// ── Saisies manuelles ────────────────────────────────────────────────────

export function EcrituresManuelles({
  plan,
  surAjout,
}: {
  plan: PlanComptable
  surAjout: () => Promise<void>
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [tx, setTx] = useState('')
  const [montant, setMontant] = useState('')
  const [libelle, setLibelle] = useState('')
  const [message, setMessage] = useState<{ texte: string; ok: boolean } | null>(null)
  const [envoi, setEnvoi] = useState(false)

  /* Seuls les types MANUELS sont proposés. Un type « auto » est déjà généré
     depuis les commandes : le saisir à la main le compterait deux fois dans
     le compte de résultat. */
  const manuels = plan.types.filter((t) => !t.auto)
  const lignes = [...plan.manuelles].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
  /* ⚠️ Le total est l'IMPACT AU RÉSULTAT, pas la somme des saisies. Seuls les
     produits et les charges y entrent : une TVA récupérable (genre `tax`) est
     une créance sur l'État, et un transit (genre `passthrough`) n'est pas à
     nous. Les compter comme des produits gonflerait le résultat d'un argent
     qui ne l'a jamais touché. */
  const impact = (genre: string | undefined, montant: number) =>
    genre === 'expense' ? -montant : genre === 'revenue' ? montant : 0
  const total = lignes.reduce(
    (s, e) => s + impact(plan.types.find((x) => x.code === e.tx)?.kind, e.amount),
    0,
  )

  async function enregistrer() {
    const m = parseInt(montant, 10) || 0
    if (!date || !tx || m <= 0 || !libelle.trim()) {
      setMessage({ texte: 'Date, type, montant et libellé sont tous obligatoires.', ok: false })
      return
    }
    setEnvoi(true)
    try {
      await ajouterEcritureManuelle({ date, tx, montant: m, libelle: libelle.trim() })
      await surAjout()
      setMontant('')
      setLibelle('')
      setMessage({ texte: 'Écriture enregistrée.', ok: true })
    } catch (e) {
      setMessage({
        texte: `Enregistrement refusé : ${e instanceof Error ? e.message : String(e)}`,
        ok: false,
      })
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-header">Nouvelle écriture</div>
        <div className="card-body">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.1fr 1.6fr 1fr 2fr auto',
              gap: 10,
              alignItems: 'end',
            }}
          >
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Date
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Type
              <div style={{ marginTop: 4 }}>
                <ListeDeroulante
                  pleineLargeur
                  valeur={tx}
                  surChangement={setTx}
                  options={[
                    { valeur: '', libelle: 'Choisir…' },
                    ...manuels.map((t) => ({ valeur: t.code, libelle: t.name })),
                  ]}
                />
              </div>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Montant (F)
              <input
                type="number"
                min={1}
                step={100}
                placeholder="0"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>
              Libellé
              <input
                type="text"
                placeholder="ex. Abonnements Premium de juillet"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                style={{ width: '100%', marginTop: 4 }}
              />
            </label>
            <button className="btn btn-accent" disabled={envoi} onClick={() => void enregistrer()}>
              Enregistrer
            </button>
          </div>
          {message && (
            <p
              style={{
                fontSize: 12.5,
                marginTop: 10,
                color: `var(${message.ok ? '--green' : '--danger'})`,
              }}
            >
              {message.texte}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          Écritures saisies
          <div className="spacer" style={{ flex: 1 }} />
          <span style={{ fontSize: 12.5, color: 'var(--n400)', fontWeight: 400 }}>
            effet net sur le résultat : <b>{fcfa(total)}</b>
          </span>
        </div>
        <div className="card-body flush">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Libellé</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
              </tr>
            </thead>
            <tbody>
              {lignes.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: 'var(--n400)', padding: 18 }}>
                    Aucune écriture saisie.
                  </td>
                </tr>
              )}
              {lignes.map((e) => {
                const t = plan.types.find((x) => x.code === e.tx)
                return (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td>
                      <span className="jrn-pill">{t?.name ?? e.tx}</span>
                    </td>
                    <td>{e.label}</td>
                    {/* Sans impact au résultat : ni vert ni rouge, et pas de
                        signe — le montant existe, il ne pèse simplement pas
                        sur le résultat. */}
                    <td
                      style={{
                        textAlign: 'right',
                        color:
                          impact(t?.kind, 1) === 0
                            ? 'var(--n400)'
                            : `var(${t?.kind === 'expense' ? '--danger' : '--green'})`,
                      }}
                    >
                      {impact(t?.kind, 1) === 0 ? '' : t?.kind === 'expense' ? '−' : '+'}
                      {fcfa(e.amount)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ── Bilan ACTIF : trois colonnes ─────────────────────────────────────────

/**
 * L'actif du bilan OHADA se présente en TROIS colonnes : brut, amortissements
 * et dépréciations, net. Le portage n'en affichait qu'une.
 *
 * ⚠️ Les amortissements valent zéro aujourd'hui — le modèle agent n'immobilise
 * rien — mais les trois colonnes sont exigées par le FORMULAIRE, pas par les
 * chiffres. Le jour où l'entreprise achète un véhicule, un état à une colonne
 * ne serait plus dépoussable tel quel.
 */
export function BilanActif({ valeurs }: { valeurs: Record<string, number> }) {
  const amortissement = (_ref: string) => 0

  return (
    <div className="card">
      <div className="card-header">Bilan — ACTIF (modèle OFFICIEL OHADA)</div>
      <div className="card-body flush">
        <table style={{ width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>Réf</th>
              <th style={{ textAlign: 'left' }}>Libellé</th>
              <th style={{ textAlign: 'right', width: 110 }}>Brut</th>
              <th style={{ textAlign: 'right', width: 110 }}>Amort./Dépréc.</th>
              <th style={{ textAlign: 'right', width: 120 }}>Net</th>
            </tr>
          </thead>
          <tbody>
            {POSTES_ACTIF.map((r) => {
              const net = valeurs[r.ref] ?? 0
              const am = amortissement(r.ref)
              const brut = net + am
              const totalisant = Boolean(r.f || r.grand)
              return (
                <tr
                  key={r.ref}
                  style={{
                    ...(r.total ? { background: 'var(--cream)' } : {}),
                    ...(totalisant ? { borderTop: '2px solid var(--n700)' } : {}),
                  }}
                >
                  <td className="acc-code" style={{ textAlign: 'center' }}>
                    {r.ref}
                  </td>
                  <td
                    style={
                      totalisant
                        ? {
                            fontWeight: r.total ? 800 : 700,
                            textTransform: 'uppercase',
                            fontSize: r.total ? 14 : 13,
                            letterSpacing: '.3px',
                          }
                        : r.head
                          ? {
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              fontSize: 11.5,
                              letterSpacing: '.4px',
                            }
                          : { paddingLeft: 14 }
                    }
                  >
                    {r.label}
                  </td>
                  <td className="num-d" style={{ textAlign: 'right' }}>
                    {brut ? fcfa(brut) : '—'}
                  </td>
                  <td style={{ textAlign: 'right', color: 'var(--n400)' }}>{am ? fcfa(am) : '—'}</td>
                  <td
                    style={{
                      textAlign: 'right',
                      fontWeight: totalisant ? (r.total ? 800 : 700) : 400,
                      fontSize: totalisant ? (r.total ? 14 : 13) : 12.5,
                    }}
                  >
                    {fcfa(net)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
